package top.wcpe.allincore.ui.runtime.element

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.pow

/**
 * 滚动动画器：每个 [ScrollableContainer] 拥有一个实例，由帧 tick 驱动，统一处理三类滚动动画：
 *
 * - **平滑滚动**（smoothScroll）：[smoothTo] 在 duration 内把 scroll 从起点 lerp 到目标。
 * - **惯性滚动**（inertia）：[fling] 以初速度进入，每帧 `velocity *= decay`，低于阈值终止；到达边界停止。
 * - **边界回弹**（bounce-back）：[bounceBack] 在内容越界时把 scroll 平滑拉回 `[0, max]`。
 *
 * 本类只负责"按时间/速度推进目标 scroll 值"，实际写入与钳制由
 * [ScrollableContainer.applyAnimatedScrollX] / [ScrollableContainer.applyAnimatedScrollY] 完成。
 * 三种动画互斥：启动任一种会清除其它两种的进行态。
 *
 * 所有时间以毫秒为单位，推进用 `dt = now - lastTickMs`，因此与帧率无关（满足风险登记
 * "PageView snap 在不同 FPS 下一致"的同源约束）。
 */
internal class ScrollAnimator {

    private enum class Mode { IDLE, SMOOTH, INERTIA, BOUNCE }

    private var mode: Mode = Mode.IDLE

    /** 平滑/回弹的起始时刻是否待首帧捕获（解耦 scrollTo 调用时的 wall-clock 与帧时钟）。 */
    private var startPending: Boolean = false

    // 惯性：速度（像素/秒）。
    private var velocityX: Float = 0f
    private var velocityY: Float = 0f
    private var inertiaDecay: Float = 0.95f

    // 平滑/回弹：起点、目标、起始时刻、时长。
    private var fromX: Float = 0f
    private var fromY: Float = 0f
    private var targetX: Float = 0f
    private var targetY: Float = 0f
    private var startMs: Long = 0L
    private var durationMs: Int = 0

    private var lastTickMs: Long = -1L

    fun isActive(): Boolean = mode != Mode.IDLE

    /** 启动平滑滚动：在 durationMs 内从当前 (x0,y0) lerp 到 (tx,ty)。 */
    fun smoothTo(x0: Float, y0: Float, tx: Float, ty: Float, durationMs: Int, nowMs: Long) {
        mode = Mode.SMOOTH
        fromX = x0
        fromY = y0
        targetX = tx
        targetY = ty
        startMs = nowMs
        this.durationMs = max(1, durationMs)
        lastTickMs = nowMs
        startPending = true
    }

    /** 启动惯性滚动：以初速度 (vx,vy) 像素/秒进入，每帧按 decay 衰减。 */
    fun fling(vx: Float, vy: Float, decay: Float, nowMs: Long) {
        mode = Mode.INERTIA
        velocityX = vx
        velocityY = vy
        inertiaDecay = decay
        lastTickMs = nowMs
    }

    /** 启动边界回弹：在固定时长内从当前 (x0,y0) 平滑拉回 (tx,ty)。 */
    fun bounceBack(x0: Float, y0: Float, tx: Float, ty: Float, nowMs: Long) {
        mode = Mode.BOUNCE
        fromX = x0
        fromY = y0
        targetX = tx
        targetY = ty
        startMs = nowMs
        durationMs = BOUNCE_DURATION_MS
        lastTickMs = nowMs
        startPending = true
    }

    /** 立即停止所有动画。 */
    fun stop() {
        mode = Mode.IDLE
        velocityX = 0f
        velocityY = 0f
        lastTickMs = -1L
        startPending = false
    }

    /**
     * 推进一帧。根据当前模式把新的 scroll 值写入 container（经其 clamp）。
     *
     * @return 是否仍在动画中（true 表示需要继续 tick）
     */
    fun tick(sc: ScrollableContainer<*>, nowMs: Long): Boolean {
        if (mode == Mode.IDLE) {
            return false
        }
        // 平滑/回弹起始时刻在首帧捕获，解耦 scrollTo 调用的 wall-clock 与帧时钟。
        if (startPending && (mode == Mode.SMOOTH || mode == Mode.BOUNCE)) {
            startMs = nowMs
            startPending = false
        }
        if (lastTickMs < 0L) {
            lastTickMs = nowMs
        }
        val dtSec = max(0f, (nowMs - lastTickMs) / 1000f)
        lastTickMs = nowMs

        when (mode) {
            Mode.SMOOTH, Mode.BOUNCE -> tickLerp(sc, nowMs)
            Mode.INERTIA -> tickInertia(sc, dtSec)
            Mode.IDLE -> { /* 已在上方短路 */ }
        }
        return mode != Mode.IDLE
    }

    private fun tickLerp(sc: ScrollableContainer<*>, nowMs: Long) {
        var t = (nowMs - startMs).toFloat() / durationMs.toFloat()
        if (t >= 1f) {
            sc.applyAnimatedScrollX(targetX)
            sc.applyAnimatedScrollY(targetY)
            mode = Mode.IDLE
            return
        }
        if (t < 0f) t = 0f
        val eased = easeOutCubic(t)
        sc.applyAnimatedScrollX(fromX + (targetX - fromX) * eased)
        sc.applyAnimatedScrollY(fromY + (targetY - fromY) * eased)
    }

    private fun tickInertia(sc: ScrollableContainer<*>, dtSec: Float) {
        val beforeX = sc.scrollX()
        val beforeY = sc.scrollY()
        sc.applyAnimatedScrollX(beforeX + velocityX * dtSec)
        sc.applyAnimatedScrollY(beforeY + velocityY * dtSec)
        // 命中边界（写入值被 clamp 截断）则该轴速度清零。
        if (sc.scrollX() == beforeX) velocityX = 0f
        if (sc.scrollY() == beforeY) velocityY = 0f
        // 帧衰减：decay 是"每 ~16ms 帧"的系数，按 dt 归一到 60fps。
        val frameFactor = inertiaDecay.toDouble().pow((dtSec * 60f).toDouble()).toFloat()
        velocityX *= frameFactor
        velocityY *= frameFactor
        if (abs(velocityX) < VELOCITY_EPSILON && abs(velocityY) < VELOCITY_EPSILON) {
            mode = Mode.IDLE
            velocityX = 0f
            velocityY = 0f
        }
    }

    companion object {
        /** 惯性速度低于该阈值（像素/秒）即终止。 */
        private const val VELOCITY_EPSILON = 4f

        /** 平滑/回弹到达目标的像素容差。 */
        private const val POSITION_EPSILON = 0.5f

        /** 回弹动画时长（毫秒）。 */
        private const val BOUNCE_DURATION_MS = 180

        /** 缓出三次方，平滑滚动/回弹的标准缓动。 */
        private fun easeOutCubic(t: Float): Float {
            val inv = 1f - t
            return 1f - inv * inv * inv
        }
    }
}
